import React, { useState, useEffect } from 'react';
import CustomerLayout from '../../components/layout/CustomerLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { customerService } from '../../services/customerService';
import {
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiShoppingBag
} from 'react-icons/fi';

const CustomerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await customerService.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerLayout title="Dashboard">
        <LoadingSpinner fullScreen />
      </CustomerLayout>
    );
  }

  if (error) {
    return (
      <CustomerLayout title="Dashboard">
        <div className="text-center text-red-600">{error}</div>
      </CustomerLayout>
    );
  }

  const { customer, merchant, recentTransactions } = dashboardData || {};

  return (
    <CustomerLayout title="Dashboard">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Outstanding */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-button flex items-center justify-center">
                <FiTrendingUp className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">Total Outstanding</h3>
            <p className="text-3xl font-bold text-neutral-900">
              {customer?.totalOutstanding?.toFixed(3)} BHD
            </p>
          </div>

          {/* Total Paid */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-button flex items-center justify-center">
                <FiTrendingDown className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">Total Paid</h3>
            <p className="text-3xl font-bold text-neutral-900">
              {customer?.totalPaid?.toFixed(3)} BHD
            </p>
          </div>

          {/* Payment Plan */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-button flex items-center justify-center">
                <FiCalendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-neutral-600 mb-1">Payment Plan</h3>
            <p className="text-2xl font-bold text-neutral-900 capitalize">
              {customer?.paymentPlan || 'None'}
            </p>
          </div>
        </div>

        {/* Merchant Info */}
        {merchant && (
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary-100 rounded-card flex items-center justify-center">
                <FiShoppingBag className="w-8 h-8 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900">{merchant.businessName}</h3>
                <p className="text-sm text-neutral-600">{merchant.mobile}</p>
                {merchant.email && (
                  <p className="text-sm text-neutral-600">{merchant.email}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="bg-white rounded-card shadow-card">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-bold text-neutral-900">Recent Transactions</h2>
          </div>
          <div className="p-6">
            {recentTransactions && recentTransactions.length > 0 ? (
              <div className="space-y-4">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction._id}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-button"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-10 h-10 rounded-button flex items-center justify-center ${
                          transaction.type === 'debt'
                            ? 'bg-red-100'
                            : 'bg-green-100'
                        }`}
                      >
                        <FiDollarSign
                          className={`w-5 h-5 ${
                            transaction.type === 'debt'
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`}
                        />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900 capitalize">
                          {transaction.type}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {new Date(transaction.createdAt).toLocaleDateString('en-GB')}
                        </p>
                        {transaction.description && (
                          <p className="text-sm text-neutral-500">{transaction.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-lg font-bold ${
                          transaction.type === 'debt'
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {transaction.type === 'debt' ? '+' : '-'}
                        {transaction.amount.toFixed(3)} BHD
                      </p>
                      <p className="text-sm text-neutral-600">
                        Balance: {transaction.balanceAfter.toFixed(3)} BHD
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-neutral-500">No recent transactions</p>
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerDashboard;
